/**
 * A promotion page's lifecycle notice and cross-link rails (web plan W0.9,
 * master D-4). Everything here is a plain `<a href>` (react-router `Link`), so
 * the links are in the rendered HTML for crawlers, not behind a click handler.
 */
import { Link } from 'react-router-dom';
import { CalendarX } from 'lucide-react';
import { PromotionCard, ShopCard, StoreCard } from '@/components/cards';
import { SectionHeader } from '@/components/ui';
import { useTranslate } from '@/i18n/LocaleLanguage';
import { availabilityScope, availabilityText, validInStores } from '@/seo/offer';
import { brandCities } from '@/seo/promotionRails';
import { cleanName } from '@/seo/text';
import { entityPath, type EntityRef } from '@/seo/urls';
import type { Promotion, PromotionMinimal, ShopMinimal, StoreMinimal } from '@/types';

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

// ─── Ended notice ───────────────────────────────────────────────────────────

export interface PromotionEndedBannerProps {
  /** `promotionNotice(…).endedNotice`: "This Mocciani offer ended on 30 Sep 2026." */
  notice: string;
  /** The brand (or shop) page, for its current offers. */
  merchant?: { name: string; path: string } | null;
  className?: string;
}

/** "This {Brand} offer ended on {date}", above the fold of an ended promotion's page. */
export function PromotionEndedBanner({ notice, merchant, className }: PromotionEndedBannerProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-2xl border border-line bg-line-light p-4',
        className,
      )}
    >
      <CalendarX size={20} className="mt-0.5 shrink-0 text-ink-secondary" aria-hidden="true" />
      <div className="min-w-0">
        <p className="font-semibold text-ink">{notice}</p>
        {merchant && (
          <Link
            to={merchant.path}
            className="mt-1 inline-block text-sm font-bold text-primary-strong hover:underline"
          >
            See current {merchant.name} offers
          </Link>
        )}
      </div>
    </div>
  );
}

// ─── Promotion rails ────────────────────────────────────────────────────────

export interface PromotionRailProps {
  /** The rail's heading (an h2): "More from Mocciani", "Similar Shoes & Footwear deals". */
  title: string;
  promotions: readonly (Promotion | PromotionMinimal)[];
  /** A link to the page that lists them all (the brand page for "More from {Brand}"). */
  more?: { to: string; label: string } | null;
  /** Said instead of the cards when the rail has none (only once its data has arrived). */
  emptyText?: string | null;
  className?: string;
}

/**
 * A row of promotion cards, each a link to its page. Renders nothing when it
 * has neither a card nor a "see all" link.
 *
 * The "see all" link sits under the cards, not in the SectionHeader: its text
 * repeats the brand ("All 1st Step Shoes & Bags offers"), and next to the
 * header's truncating h2 it left "More …" on a 375 px phone.
 */
export function PromotionRail({ title, promotions, more, emptyText, className }: PromotionRailProps) {
  if (promotions.length === 0 && !more) return null;
  return (
    <section aria-label={title} className={className}>
      <SectionHeader title={title} />
      {promotions.length > 0 ? (
        <ul className="mt-4 flex gap-4 overflow-x-auto scrollbar-none pb-2 pt-1">
          {promotions.map((promotion) => (
            <li key={promotion.id} className="shrink-0">
              <PromotionCard promotion={promotion} variant="compact" />
            </li>
          ))}
        </ul>
      ) : emptyText ? (
        <p className="mt-3 text-sm text-ink-secondary">{emptyText}</p>
      ) : null}
      {more && (
        <Link
          to={more.to}
          className="mt-3 inline-block text-sm font-bold text-primary-strong hover:underline"
        >
          {more.label}
        </Link>
      )}
    </section>
  );
}

// ─── Where it's valid ───────────────────────────────────────────────────────

export interface WhereItsValidProps {
  /** The stores the promotion names. */
  stores: readonly StoreMinimal[];
  /** The shops the promotion names. */
  shops: readonly ShopMinimal[];
  /** Its brand, with the brand's stores (id + city) for a brand-wide promotion. */
  brand?: (EntityRef & { name: string; brandStores?: readonly { id: string; city?: string | null }[] | null }) | null;
  /** Backend B3.5 `availabilityScope`: where it can be used. */
  scope?: string | null;
  /** Who runs it (the brand, or a shop-only promotion's shop), for the availability line. */
  merchantName?: string | null;
  className?: string;
}

/**
 * "Where it's valid": the backend's availability line when staff set one
 * ("Valid at all Bata stores", "Online only", …; backend B3.5), then the
 * stores and shops the promotion names, each a link to its page; for a
 * brand-wide promotion that names none, the cities of its brand's stores and
 * a link to the brand page, which lists every store. The cities are plain
 * text until brand × city pages exist (plan W3). An online-only offer lists
 * no store, shop or city, and an offer valid at selected stores that names
 * none does not list all the brand's cities. Nothing when there is nothing to
 * say.
 */
export function WhereItsValid({ stores, shops, brand, scope, merchantName, className }: WhereItsValidProps) {
  const title = "Where it's valid";
  const t = useTranslate();
  const availability = availabilityText(scope, merchantName, t);
  const inStores = validInStores(scope);
  const line = availability ? <p className="text-sm font-semibold text-ink">{availability}</p> : null;

  if (inStores && (stores.length > 0 || shops.length > 0)) {
    return (
      <section aria-label={title} className={cn('space-y-3', className)}>
        <SectionHeader title={title} />
        {line}
        <div className="grid grid-cols-2 justify-items-center gap-3">
          {stores.map((store) => (
            <StoreCard key={store.id} store={store} compact />
          ))}
          {shops.map((shop) => (
            <ShopCard key={shop.id} shop={shop} compact />
          ))}
        </div>
      </section>
    );
  }

  const name = cleanName(brand?.name);
  // The brand's cities stand for "all its stores": not for an online-only
  // offer, nor for one valid at selected stores only.
  const cities = inStores && availabilityScope(scope) !== 'SELECTED_STORES' ? brandCities(brand?.brandStores) : [];
  if (!brand || !name || cities.length === 0) {
    return line ? (
      <section aria-label={title} className={cn('space-y-3', className)}>
        <SectionHeader title={title} />
        {line}
      </section>
    ) : null;
  }
  const storeCount = brand.brandStores?.length ?? 0;

  return (
    <section aria-label={title} className={cn('space-y-3', className)}>
      <SectionHeader title={title} />
      {line}
      <p className="text-sm text-ink-secondary">
        {name} stores in {cities.length} {cities.length === 1 ? 'city' : 'cities'}:
      </p>
      <ul className="flex flex-wrap gap-2">
        {cities.map(({ city, stores: count }) => (
          <li
            key={city}
            className="rounded-full border border-line px-3 py-1 text-sm font-semibold text-ink"
          >
            {city} <span className="font-normal text-ink-secondary">({count})</span>
          </li>
        ))}
      </ul>
      <Link
        to={entityPath(brand, 'brand')}
        className="inline-block text-sm font-bold text-primary-strong hover:underline"
      >
        See all {storeCount} {name} {storeCount === 1 ? 'store' : 'stores'}
      </Link>
    </section>
  );
}
